import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import HelpSection from './HelpSection';

export default function OptimizationRulesEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [draft, setDraft] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config) {
      setDraft({
        optimizationStrategy: config.engineSettings?.optimizationStrategy || 'employee',
        balancedWeightEmployee: config.engineSettings?.balancedWeightEmployee || 50,
        balancedWeightEmployer: config.engineSettings?.balancedWeightEmployer || 50,
        minEmployeeNetBenefit: config.engineSettings?.minEmployeeNetBenefit || 0,
        maxPremiumPercentOfSalary: config.engineSettings?.maxPremiumPercentOfSalary || 40,
        salaryFloorMethod: config.engineSettings?.salaryFloorMethod || 'auto',
        manualSalaryFloors: config.engineSettings?.manualSalaryFloors || {},
        preferLowerPlansForLowEarners: config.engineSettings?.preferLowerPlansForLowEarners || false,
        lowEarnerThreshold: config.engineSettings?.lowEarnerThreshold || 35000,
        defaultEnrollmentRate: config.engineSettings?.defaultEnrollmentRate || 80,
        includeFUTA: config.engineSettings?.includeFUTA || false,
        employerFICAMethod: config.engineSettings?.employerFICAMethod || 'simplified',
        defaultPayFrequency: config.engineSettings?.payPeriodsPerYear || 12,
        roundingMethod: config.engineSettings?.roundingMethod || 'nearest_cent',
        annualToPeriodMethod: config.engineSettings?.annualToPeriodMethod || 'divide_then_round',
        displayPrecision: config.engineSettings?.displayPrecision || 2,
        breakEvenSearchMin: config.engineSettings?.breakEvenSearchMin || 15000,
        breakEvenSearchMax: config.engineSettings?.breakEvenSearchMax || 250000,
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const save = async () => {
    try {
      await updateConfig({
        engineSettings: {
          ...config.engineSettings,
          optimizationStrategy: draft.optimizationStrategy,
          balancedWeightEmployee: draft.balancedWeightEmployee,
          balancedWeightEmployer: draft.balancedWeightEmployer,
          minEmployeeNetBenefit: draft.minEmployeeNetBenefit,
          maxPremiumPercentOfSalary: draft.maxPremiumPercentOfSalary,
          salaryFloorMethod: draft.salaryFloorMethod,
          manualSalaryFloors: draft.manualSalaryFloors,
          preferLowerPlansForLowEarners: draft.preferLowerPlansForLowEarners,
          lowEarnerThreshold: draft.lowEarnerThreshold,
          defaultEnrollmentRate: draft.defaultEnrollmentRate,
          includeFUTA: draft.includeFUTA,
          employerFICAMethod: draft.employerFICAMethod,
          payPeriodsPerYear: draft.defaultPayFrequency,
          roundingMethod: draft.roundingMethod,
          annualToPeriodMethod: draft.annualToPeriodMethod,
          displayPrecision: draft.displayPrecision,
          breakEvenSearchMin: draft.breakEvenSearchMin,
          breakEvenSearchMax: draft.breakEvenSearchMax,
        }
      });
      setHasChanges(false);
      showToast('Optimization rules saved successfully', 'success');
    } catch (err) {
      showToast('Failed to save optimization rules', 'error');
    }
  };

  const cancel = () => {
    setDraft({
      optimizationStrategy: config.engineSettings?.optimizationStrategy || 'employee',
      balancedWeightEmployee: config.engineSettings?.balancedWeightEmployee || 50,
      balancedWeightEmployer: config.engineSettings?.balancedWeightEmployer || 50,
      minEmployeeNetBenefit: config.engineSettings?.minEmployeeNetBenefit || 0,
      maxPremiumPercentOfSalary: config.engineSettings?.maxPremiumPercentOfSalary || 40,
      salaryFloorMethod: config.engineSettings?.salaryFloorMethod || 'auto',
      manualSalaryFloors: config.engineSettings?.manualSalaryFloors || {},
      preferLowerPlansForLowEarners: config.engineSettings?.preferLowerPlansForLowEarners || false,
      lowEarnerThreshold: config.engineSettings?.lowEarnerThreshold || 35000,
      defaultEnrollmentRate: config.engineSettings?.defaultEnrollmentRate || 80,
      includeFUTA: config.engineSettings?.includeFUTA || false,
      employerFICAMethod: config.engineSettings?.employerFICAMethod || 'simplified',
      defaultPayFrequency: config.engineSettings?.payPeriodsPerYear || 12,
      roundingMethod: config.engineSettings?.roundingMethod || 'nearest_cent',
      annualToPeriodMethod: config.engineSettings?.annualToPeriodMethod || 'divide_then_round',
      displayPrecision: config.engineSettings?.displayPrecision || 2,
      breakEvenSearchMin: config.engineSettings?.breakEvenSearchMin || 15000,
      breakEvenSearchMax: config.engineSettings?.breakEvenSearchMax || 250000,
    });
    setHasChanges(false);
  };

  if (!draft) return <div>Loading...</div>;

  const planTiers = config.planTiers || [];
  const activePlans = planTiers.filter(p => !p.archived);

  const helpSections = [
    {
      heading: 'What is this?',
      content: 'Optimization Rules control how the recommendation engine selects the "best" plan tier for each employee based on their salary, state, and filing status. These settings define the strategy, constraints, and special cases the engine uses when analyzing thousands of salary/plan combinations.'
    },
    {
      heading: 'Optimization Strategy',
      content: 'Determines the primary goal when selecting plans:',
      impacts: [
        'Employee: Maximizes net take-home pay increase per period',
        'Employer: Maximizes total FICA savings across the workforce',
        'Balanced: Weighted average of both (you set the ratio)'
      ]
    },
    {
      heading: 'Guardrails',
      content: 'Safety limits that filter out plans before optimization:',
      impacts: [
        'Max Premium % of Salary: Excludes plans where the annual premium exceeds this percentage (prevents recommending $10K plan to $30K earner)',
        'Salary Floor per Tier: Auto-calculated or manual minimums prevent low earners from being matched to high-premium tiers',
        'Min Employee Net Benefit: Plans must provide at least this much net benefit per pay period to be viable'
      ]
    },
    {
      heading: 'Low Earner Policy',
      content: 'When enabled, employees under the threshold are automatically assigned the lowest viable plan tier (less paycheck disruption). Overrides the optimization strategy for this group.',
      impacts: [
        'Workforce tab: Low earners get conservative recommendations',
        'Individual tab: Shows why a lower plan was chosen despite higher-tier viability'
      ]
    },
    {
      heading: 'Forecasting Settings',
      content: 'Assumptions used in workforce-level projections:',
      impacts: [
        'Default Enrollment Rate: What % of eligible employees will actually enroll (used for employer savings forecasts)',
        'Include FUTA: Whether to count Federal Unemployment Tax savings in employer total',
        'Employer FICA Method: Simplified (flat 7.65%) vs Wage-Base Aware (accounts for SS cap on high earners)'
      ]
    },
    {
      heading: 'Display Settings',
      content: 'How calculations are presented in the UI and PDFs:',
      impacts: [
        'Pay Frequency: Default periods per year (can be overridden per analysis)',
        'Rounding: How per-period amounts are rounded (nearest cent, truncate, etc.)',
        'Display Precision: Decimal places shown in reports'
      ]
    }
  ];

  return (
    <div style={S.card}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>Optimization Rules</h2>
      <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>Controls how the engine recommends plan tiers for employees.</p>

      <HelpSection title="How do Optimization Rules work?" sections={helpSections} />

      {/* Strategy */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Strategy</h3>
        
        <label style={S.label}>Default Optimization Strategy</label>
        <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>How the engine selects the "best" plan for each employee</p>
        <select 
          style={S.select} 
          value={draft.optimizationStrategy}
          onChange={e => handleChange('optimizationStrategy', e.target.value)}
        >
          <option value="employee">Maximize Employee Benefit</option>
          <option value="employer">Maximize Employer Savings</option>
          <option value="balanced">Balanced</option>
        </select>

        {draft.optimizationStrategy === 'balanced' && (
          <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 8 }}>
            <label style={S.label}>Balanced Weighting</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...S.label, fontSize: 11 }}>Employee Weight</label>
                <input 
                  type="number" 
                  style={S.input}
                  value={draft.balancedWeightEmployee}
                  onChange={e => {
                    const val = parseInt(e.target.value) || 0;
                    handleChange('balancedWeightEmployee', val);
                    handleChange('balancedWeightEmployer', 100 - val);
                  }}
                  min="0"
                  max="100"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...S.label, fontSize: 11 }}>Employer Weight</label>
                <input 
                  type="number" 
                  style={S.input}
                  value={draft.balancedWeightEmployer}
                  onChange={e => {
                    const val = parseInt(e.target.value) || 0;
                    handleChange('balancedWeightEmployer', val);
                    handleChange('balancedWeightEmployee', 100 - val);
                  }}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={S.label}>Default Min Employee Net Benefit</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Minimum per-period take-home increase for viability</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
            <input 
              type="number" 
              style={{ ...S.input, width: 120 }}
              value={draft.minEmployeeNetBenefit}
              onChange={e => handleChange('minEmployeeNetBenefit', parseFloat(e.target.value) || 0)}
              step="0.01"
            />
            <span style={{ fontSize: 13, color: C.textMid }}>/period</span>
          </div>
        </div>
      </div>

      {/* Guardrails */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Guardrails</h3>
        
        <label style={S.label}>Maximum Premium as % of Salary</label>
        <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Plans exceeding this percentage are excluded</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input 
            type="number" 
            style={{ ...S.input, width: 100 }}
            value={draft.maxPremiumPercentOfSalary}
            onChange={e => handleChange('maxPremiumPercentOfSalary', parseFloat(e.target.value) || 0)}
            min="0"
            max="100"
          />
          <span style={{ fontSize: 14, fontWeight: 600 }}>%</span>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={S.label}>Salary Floor per Tier</label>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input 
                type="radio" 
                checked={draft.salaryFloorMethod === 'auto'}
                onChange={() => handleChange('salaryFloorMethod', 'auto')}
              />
              Auto-calculate
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input 
                type="radio" 
                checked={draft.salaryFloorMethod === 'manual'}
                onChange={() => handleChange('salaryFloorMethod', 'manual')}
              />
              Manual minimums
            </label>
          </div>

          {draft.salaryFloorMethod === 'manual' && (
            <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 8 }}>
              {activePlans.map(plan => (
                <div key={plan.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, width: 100 }}>{plan.label}:</span>
                  <span style={{ fontSize: 13 }}>min salary $</span>
                  <input 
                    type="number" 
                    style={{ ...S.input, width: 120 }}
                    value={draft.manualSalaryFloors[plan.id] || ''}
                    onChange={e => handleChange('manualSalaryFloors', {
                      ...draft.manualSalaryFloors,
                      [plan.id]: parseFloat(e.target.value) || 0
                    })}
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Low Earner Policy */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Low Earner Policy</h3>
        
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>
          <input 
            type="checkbox" 
            checked={draft.preferLowerPlansForLowEarners}
            onChange={e => handleChange('preferLowerPlansForLowEarners', e.target.checked)}
          />
          <div>
            <div style={{ fontWeight: 600 }}>Prefer Lower Plans for Low Earners</div>
            <div style={{ fontSize: 11, color: C.textMid }}>Recommend lowest viable plan for employees under threshold (less paycheck disruption)</div>
          </div>
        </label>

        {draft.preferLowerPlansForLowEarners && (
          <div>
            <label style={S.label}>Low Earner Threshold</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
              <input 
                type="number" 
                style={{ ...S.input, width: 120 }}
                value={draft.lowEarnerThreshold}
                onChange={e => handleChange('lowEarnerThreshold', parseFloat(e.target.value) || 0)}
              />
              <span style={{ fontSize: 13, color: C.textMid }}>annual salary</span>
            </div>
          </div>
        )}
      </div>

      {/* Forecasting */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Forecasting</h3>
        
        <label style={S.label}>Default Enrollment Rate Assumption</label>
        <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Used for workforce-level projections (100% = assume all eligible enroll)</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input 
            type="number" 
            style={{ ...S.input, width: 100 }}
            value={draft.defaultEnrollmentRate}
            onChange={e => handleChange('defaultEnrollmentRate', parseFloat(e.target.value) || 0)}
            min="0"
            max="100"
          />
          <span style={{ fontSize: 14, fontWeight: 600 }}>%</span>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={draft.includeFUTA}
              onChange={e => handleChange('includeFUTA', e.target.checked)}
            />
            Include FUTA in Employer Savings (default)
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={S.label}>Employer FICA Calculation Method</label>
          <select 
            style={S.select}
            value={draft.employerFICAMethod}
            onChange={e => handleChange('employerFICAMethod', e.target.value)}
          >
            <option value="simplified">Simplified — flat 7.65% on full premium</option>
            <option value="wage_base_aware">Wage-Base Aware — accounts for SS cap on high earners</option>
          </select>
        </div>
      </div>

      {/* Display */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Display</h3>
        
        <label style={S.label}>Default Pay Frequency (display)</label>
        <select 
          style={S.select}
          value={draft.defaultPayFrequency}
          onChange={e => handleChange('defaultPayFrequency', parseInt(e.target.value))}
        >
          <option value="12">Monthly (12)</option>
          <option value="24">Semi-Monthly (24)</option>
          <option value="26">Bi-Weekly (26)</option>
          <option value="52">Weekly (52)</option>
        </select>

        <div style={{ marginTop: 12 }}>
          <label style={S.label}>Rounding Method</label>
          <select 
            style={S.select}
            value={draft.roundingMethod}
            onChange={e => handleChange('roundingMethod', e.target.value)}
          >
            <option value="nearest_cent">Round to nearest cent</option>
            <option value="truncate">Truncate</option>
            <option value="round_up">Round up</option>
            <option value="nearest_nickel">Round to nearest 5 cents</option>
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={S.label}>Annual-to-Period Method</label>
          <select 
            style={S.select}
            value={draft.annualToPeriodMethod}
            onChange={e => handleChange('annualToPeriodMethod', e.target.value)}
          >
            <option value="divide_then_round">Divide then round</option>
            <option value="round_then_divide">Round then divide</option>
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={S.label}>Display Precision</label>
          <select 
            style={S.select}
            value={draft.displayPrecision}
            onChange={e => handleChange('displayPrecision', parseInt(e.target.value))}
          >
            <option value="0">0 decimal places</option>
            <option value="2">2 decimal places</option>
            <option value="4">4 decimal places</option>
          </select>
        </div>
      </div>

      {/* Break-Even Search */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Break-Even Search</h3>
        
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Search Minimum Salary</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
              <input 
                type="number" 
                style={S.input}
                value={draft.breakEvenSearchMin}
                onChange={e => handleChange('breakEvenSearchMin', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Search Maximum Salary</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
              <input 
                type="number" 
                style={S.input}
                value={draft.breakEvenSearchMax}
                onChange={e => handleChange('breakEvenSearchMax', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save/Cancel */}
      {hasChanges && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancel} style={S.buttonSecondary}>Cancel</button>
          <button data-testid="save-optimization-rules-btn" onClick={save} style={S.buttonPrimary}>Save Changes</button>
        </div>
      )}
    </div>
  );
}
